import React, { useEffect, useState, useCallback } from 'react';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function StatusBadge({ status }) {
  const c = {
    draft: { bg: '#F0F0F0', fg: '#666' },
    scheduled: { bg: '#EEF2FF', fg: '#4338ca' },
    running: { bg: '#E8F5EC', fg: '#1a7f3c' },
    paused: { bg: '#FDF6E3', fg: '#a16207' },
    completed: { bg: '#E8E8E8', fg: '#333' },
    stopped: { bg: '#FBE9E9', fg: '#b91c1c' },
  }[status] || { bg: '#F0F0F0', fg: '#666' };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, textTransform: 'capitalize' }}>
      {status || 'unknown'}
    </span>
  );
}

function Progress({ sent, total }) {
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#F0EDE9', borderRadius: 3, overflow: 'hidden', minWidth: 60 }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#8B1A1A' }} />
      </div>
      <span style={{ fontSize: 12, color: '#666', minWidth: 70 }}>{sent}/{total}</span>
    </div>
  );
}

export default function Campaigns() {
  const ctx = useOutletContext() || {};
  const { clientId, isGlobalMode } = ctx;
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const url = isGlobalMode
        ? `${API_BASE}/admin/campaigns`
        : `${API_BASE}/clients/${clientId}/campaigns`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setCampaigns(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, isGlobalMode, getToken]);

  useEffect(() => {
    if (isGlobalMode || clientId) load();
  }, [isGlobalMode, clientId, load]);

  const filtered = campaigns.filter(c => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.status || '').toLowerCase().includes(q) ||
      (c.client?.name || '').toLowerCase().includes(q) ||
      (c.client?.sector || '').toLowerCase().includes(q)
    );
  });

  if (loading) return <div style={{ padding: 24 }}>Loading campaigns…</div>;
  if (error) return <div style={{ padding: 24, color: '#b91c1c' }}>Error: {error}</div>;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
          Campaigns {isGlobalMode && <span style={{ fontSize: 13, color: '#666', fontWeight: 400, marginLeft: 8 }}>· All clients</span>}
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            type="text"
            placeholder="Search campaigns…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #d4d4d4', borderRadius: 6, fontSize: 14, width: 260 }}
          />
          {!isGlobalMode && (
            <button
              onClick={() => setCreateOpen(true)}
              style={{ background: '#8B1A1A', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
            >
              + New campaign
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: '#666', border: '1px dashed #d4d4d4', borderRadius: 8 }}>
          {campaigns.length === 0 ? 'No campaigns yet.' : 'No campaigns match that search.'}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e5e5', color: '#666' }}>
              {isGlobalMode && <th style={{ padding: '10px 12px', fontWeight: 500 }}>Client</th>}
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Name</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Status</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Progress</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Replies</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Bounces</th>
              <th style={{ padding: '10px 12px', fontWeight: 500 }}>Created</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr
                key={c.id}
                onClick={() => {
                  const base = isGlobalMode ? '/mail/campaigns' : `/clients/${clientId}/mail/campaigns`;
                  navigate(`${base}/${c.id}`);
                }}
                style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                {isGlobalMode && (
                  <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                    {c.client ? (
                      <Link to={`/clients/${c.client.id}/mail/campaigns`} style={{ color: '#8B1A1A', textDecoration: 'none' }}>
                        <div>{c.client.name}</div>
                        {c.client.sector && <div style={{ fontSize: 11, color: '#888' }}>{c.client.sector}</div>}
                      </Link>
                    ) : (
                      <span style={{ color: '#aaa' }}>—</span>
                    )}
                  </td>
                )}
                <td style={{ padding: '10px 12px', fontWeight: 500 }}>{c.name}</td>
                <td style={{ padding: '10px 12px' }}><StatusBadge status={c.status} /></td>
                <td style={{ padding: '10px 12px', minWidth: 160 }}>
                  <Progress sent={c.sent_count || 0} total={c.total_contacts || 0} />
                </td>
                <td style={{ padding: '10px 12px' }}>{c.reply_count || 0}</td>
                <td style={{ padding: '10px 12px' }}>{c.bounce_count || 0}</td>
                <td style={{ padding: '10px 12px', color: '#666' }}>
                  {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {createOpen && (
        <CreateCampaignModal
          clientId={clientId}
          onClose={() => setCreateOpen(false)}
          onCreated={(newId) => {
            setCreateOpen(false);
            navigate(`/clients/${clientId}/mail/campaigns/${newId}`);
          }}
        />
      )}
    </div>
  );
}

function CreateCampaignModal({ clientId, onClose, onCreated }) {
  const { getToken } = useAuth();
  const [step, setStep] = useState(1); // 1 = basics, 2 = mailboxes
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pools, setPools] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [selectedMailboxIds, setSelectedMailboxIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [mbQuery, setMbQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [pRes, mRes] = await Promise.all([
          fetch(`${API_BASE}/clients/${clientId}/pools`, { headers }),
          fetch(`${API_BASE}/clients/${clientId}/mailboxes`, { headers }),
        ]);
        if (pRes.ok) setPools(await pRes.json());
        if (mRes.ok) {
          const data = await mRes.json();
          const list = Array.isArray(data) ? data : data.mailboxes || [];
          setMailboxes(list);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [clientId, getToken]);

  const loadPool = async (poolId) => {
    if (!poolId) return;
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/pools/${poolId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('failed to load pool');
      const data = await r.json();
      const ids = (data.members || data.mailboxes || []).map(m => m.mailbox_id || m.id);
      setSelectedMailboxIds(new Set([...selectedMailboxIds, ...ids]));
    } catch (e) { setError(e.message); }
  };

  const toggleMailbox = (id) => {
    const next = new Set(selectedMailboxIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedMailboxIds(next);
  };

  const selectAll = () => setSelectedMailboxIds(new Set(filteredMailboxes.map(m => m.id)));
  const clearAll = () => setSelectedMailboxIds(new Set());

  const filteredMailboxes = mailboxes.filter(m => {
    if (providerFilter !== 'all' && m.provider !== providerFilter) return false;
    if (mbQuery) {
      const q = mbQuery.toLowerCase();
      if (!(m.email_address || '').toLowerCase().includes(q) &&
          !(m.display_name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const providers = Array.from(new Set(mailboxes.map(m => m.provider).filter(Boolean)));

  const create = async () => {
    if (!name.trim()) { setError('Name required'); setStep(1); return; }
    if (selectedMailboxIds.size === 0) { setError('Pick at least one mailbox'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      // 1. Create campaign with first mailbox (legacy column requires non-null pool_id or mailbox_id)
      const firstMailboxId = Array.from(selectedMailboxIds)[0];
      const createRes = await fetch(`${API_BASE}/clients/${clientId}/campaigns`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          mailbox_id: firstMailboxId,
        }),
      });
      if (!createRes.ok) {
        const err = await createRes.json().catch(() => ({}));
        throw new Error(err.error || `Create failed (${createRes.status})`);
      }
      const created = await createRes.json();
      const campaignId = created.campaign.id;

      // 2. Attach all selected mailboxes to junction table
      const attachRes = await fetch(`${API_BASE}/campaigns/${campaignId}/mailboxes`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mailbox_ids: Array.from(selectedMailboxIds) }),
      });
      if (!attachRes.ok) {
        const err = await attachRes.json().catch(() => ({}));
        throw new Error(err.error || `Mailbox attach failed (${attachRes.status})`);
      }

      onCreated(campaignId);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
  };
  const dialogStyle = {
    background: 'white', borderRadius: 10, padding: 24, width: 600, maxWidth: '92vw',
    maxHeight: '88vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  };
  const inputStyleLocal = {
    width: '100%', padding: '8px 10px', border: '1px solid #D4CFC9',
    borderRadius: 5, fontSize: 13, boxSizing: 'border-box',
  };

  const statusColour = (s) => s === 'active' ? '#1a7f3c' : s === 'error' ? '#b91c1c' : s === 'paused' ? '#a16207' : '#888';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>New campaign</h3>
          <div style={{ fontSize: 12, color: '#888' }}>Step {step} of 2</div>
        </div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
          <div style={{ flex: 1, height: 3, background: step >= 1 ? '#8B1A1A' : '#EAE6E1', borderRadius: 2 }} />
          <div style={{ flex: 1, height: 3, background: step >= 2 ? '#8B1A1A' : '#EAE6E1', borderRadius: 2 }} />
        </div>

        {loading ? (
          <div style={{ color: '#888' }}>Loading…</div>
        ) : (
          <>
            {step === 1 && (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="e.g. Q1 outreach — UK accountants" style={inputStyleLocal} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={labelStyle}>Description (optional)</label>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this campaign for?" style={inputStyleLocal} />
                </div>
              </div>
            )}

            {step === 2 && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <label style={labelStyle}>Sending mailboxes</label>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 10 }}>
                  Pick the mailboxes this campaign will rotate across. {selectedMailboxIds.size > 0 && <strong>{selectedMailboxIds.size} selected.</strong>}
                </div>

                <PoolPicker pools={pools} onPick={loadPool} />

                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <input type="text" placeholder="Search mailboxes…" value={mbQuery} onChange={(e) => setMbQuery(e.target.value)} style={{ ...inputStyleLocal, flex: 1 }} />
                  {providers.length > 1 && (
                    <select value={providerFilter} onChange={(e) => setProviderFilter(e.target.value)} style={{ ...inputStyleLocal, width: 130 }}>
                      <option value="all">All providers</option>
                      {providers.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 12 }}>
                  <button type="button" onClick={selectAll} style={{ background: 'none', border: 'none', color: '#8B1A1A', cursor: 'pointer', padding: 0, fontSize: 12 }}>
                    Select all ({filteredMailboxes.length})
                  </button>
                  <button type="button" onClick={clearAll} style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 0, fontSize: 12 }}>
                    Clear
                  </button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #EAE6E1', borderRadius: 6, minHeight: 200 }}>
                  {filteredMailboxes.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
                      {mailboxes.length === 0 ? 'No mailboxes connected. Add one in Mail → Mailboxes first.' : 'No mailboxes match.'}
                    </div>
                  ) : filteredMailboxes.map(m => {
                    const checked = selectedMailboxIds.has(m.id);
                    const disabled = m.status === 'disconnected';
                    return (
                      <label key={m.id} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        borderBottom: '1px solid #F5F2EE', cursor: disabled ? 'not-allowed' : 'pointer',
                        background: checked ? '#FBF1F1' : 'white', opacity: disabled ? 0.5 : 1,
                      }}>
                        <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleMailbox(m.id)} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.email_address}
                            {m.display_name && <span style={{ color: '#888', marginLeft: 6 }}>— {m.display_name}</span>}
                          </div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2, display: 'flex', gap: 8 }}>
                            <span style={{ textTransform: 'capitalize' }}>{m.provider || '—'}</span>
                            <span>·</span>
                            <span style={{ color: statusColour(m.status) }}>{m.status || 'unknown'}</span>
                            <span>·</span>
                            <span>cap {m.daily_cap ?? '—'}</span>
                            {m.owner_type === 'webcite' && <><span>·</span><span style={{ color: '#8B1A1A' }}>shared</span></>}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {error && <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 12 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 18 }}>
              <button onClick={onClose} disabled={saving} style={btnSecondaryModal}>Cancel</button>
              <div style={{ display: 'flex', gap: 8 }}>
                {step === 2 && (
                  <button onClick={() => setStep(1)} disabled={saving} style={btnSecondaryModal}>Back</button>
                )}
                {step === 1 && (
                  <button
                    onClick={() => {
                      if (!name.trim()) { setError('Name required'); return; }
                      setError(null);
                      setStep(2);
                    }}
                    style={btnPrimaryModal}
                  >Next: pick mailboxes</button>
                )}
                {step === 2 && (
                  <button onClick={create} disabled={saving || selectedMailboxIds.size === 0} style={{ ...btnPrimaryModal, opacity: selectedMailboxIds.size === 0 ? 0.5 : 1 }}>
                    {saving ? 'Creating…' : `Create campaign (${selectedMailboxIds.size})`}
                  </button>
                )}
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
              Created as <strong>draft</strong>. Configure schedule and audience on the next screen, then launch.
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// Deterministic pastel from pool id — same pool always same colour
const POOL_PALETTE = [
  { bg: '#FBF1F1', border: '#E6C8C8', fg: '#8B1A1A' },
  { bg: '#EEF2FF', border: '#C7D2FE', fg: '#4338ca' },
  { bg: '#E8F5EC', border: '#BBE5C5', fg: '#1a7f3c' },
  { bg: '#FDF6E3', border: '#F5E0A8', fg: '#a16207' },
  { bg: '#FBE9E9', border: '#F2C5C5', fg: '#b91c1c' },
  { bg: '#F0F0F0', border: '#D4D4D4', fg: '#555' },
  { bg: '#F5E6FF', border: '#DDC3F0', fg: '#6B21A8' },
  { bg: '#E0F2F1', border: '#A7DCD9', fg: '#0F766E' },
  { bg: '#FFF1E6', border: '#F5D5B8', fg: '#9A4F0F' },
  { bg: '#E6F4FF', border: '#B8DBF5', fg: '#0369A1' },
];
function poolColour(id) {
  const n = typeof id === 'number' ? id : parseInt(id) || 0;
  return POOL_PALETTE[n % POOL_PALETTE.length];
}

function PoolPicker({ pools, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  if (!pools || pools.length === 0) return null;

  // Inline pills when <= 3 pools
  if (pools.length <= 3) {
    return (
      <div style={{ marginBottom: 12, padding: 10, background: '#FBF9F7', borderRadius: 6, border: '1px solid #EAE6E1' }}>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Quick add from pool:</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {pools.map(p => {
            const c = poolColour(p.id);
            return (
              <button key={p.id} type="button" onClick={() => onPick(p.id)}
                style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 5, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: c.fg, fontWeight: 500 }}>
                + {p.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Searchable dropdown when > 3 pools
  const filtered = pools.filter(p => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);
  });

  return (
    <div style={{ marginBottom: 12, position: 'relative' }}>
      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Quick add from pool ({pools.length} available):</div>
      <input
        type="text"
        placeholder="Search pools by name…"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{ width: '100%', padding: '8px 10px', border: '1px solid #D4CFC9', borderRadius: 5, fontSize: 13, boxSizing: 'border-box' }}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          background: 'white', border: '1px solid #EAE6E1', borderRadius: 6,
          boxShadow: '0 6px 20px rgba(0,0,0,0.08)', zIndex: 60,
          maxHeight: 280, overflowY: 'auto', padding: 4,
        }}>
          {filtered.slice(0, 50).map(p => {
            const c = poolColour(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(p.id); setQuery(''); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  width: '100%', padding: '8px 10px', textAlign: 'left',
                  background: 'transparent', border: 'none', borderRadius: 5,
                  cursor: 'pointer', fontSize: 13,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, flexShrink: 0 }}>
                  {p.name}
                </span>
                <span style={{ color: '#888', fontSize: 11 }}>
                  {p.members?.length ?? p.member_count ?? 0} mailbox{(p.members?.length ?? p.member_count ?? 0) !== 1 ? 'es' : ''}
                </span>
              </button>
            );
          })}
          {filtered.length > 50 && (
            <div style={{ padding: 8, fontSize: 11, color: '#888', textAlign: 'center' }}>Showing first 50 of {filtered.length}. Refine search.</div>
          )}
        </div>
      )}
      {open && filtered.length === 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 2px)', left: 0, right: 0,
          background: 'white', border: '1px solid #EAE6E1', borderRadius: 6,
          padding: 12, fontSize: 13, color: '#888', zIndex: 60,
        }}>No pools match.</div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 };
const btnPrimaryModal = { background: '#8B1A1A', color: 'white', border: 'none', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnSecondaryModal = { background: 'white', color: '#333', border: '1px solid #D4CFC9', borderRadius: 5, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
